// indexed db storage
import { useState, useEffect } from "react";
import { getImageFromDB, saveImageToDB } from "../utility/indexedDB";

const MAX_SIZE = 192;           // longest edge in px; plenty for a ~60px tile at 3x
const memoryCache = new Map();  // url -> object URL, lives for the session
const inflight    = new Map();  // url -> Promise, so duplicate cards share one load

const loadImage = (src, crossOrigin) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload  = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const downscaleToBlob = (img) =>
  new Promise((resolve, reject) => {
    const w = img.naturalWidth  || img.width;
    const h = img.naturalHeight || img.height;
    const scale  = Math.min(1, MAX_SIZE / Math.max(w, h, 1));
    const canvas = document.createElement("canvas");
    canvas.width  = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      "image/webp",   // keeps transparency; Safari falls back to PNG automatically
      0.8
    );
  });

const resolveImage = async (url) => {
  if (memoryCache.has(url)) return memoryCache.get(url);
  if (inflight.has(url))    return inflight.get(url);

  const job = (async () => {
    // 1. IndexedDB
    let cached = null;
    try { cached = await getImageFromDB(url); } catch (_) { /* treat as a miss */ }

    if (cached instanceof Blob) {
      const objUrl = URL.createObjectURL(cached);
      memoryCache.set(url, objUrl);
      return objUrl;
    }

    // 2. Legacy entry (full-size base64 string): re-encode it once, no network needed.
    // 3. No entry: download it.
    let img;
    if (typeof cached === "string" && cached) {
      img = await loadImage(cached);
    } else {
      try {
        img = await loadImage(url, "Anonymous");
      } catch {
        await loadImage(url);   // CORS blocked but the image exists: show it, skip caching
        return url;
      }
    }

    // 4. Shrink, then save (caching is best-effort)
    let blob;
    try { blob = await downscaleToBlob(img); } catch { return url; }
    try { await saveImageToDB(url, blob); } catch (_) {}

    const objUrl = URL.createObjectURL(blob);
    memoryCache.set(url, objUrl);
    return objUrl;
  })();

  inflight.set(url, job);
  try { return await job; } finally { inflight.delete(url); }
};

const initialState = (url) => {
  const cached = url && memoryCache.get(url);
  return { image: cached || null, loading: !!url && !cached, error: !url };
};

// returns the same object when nothing changed, so React skips the re-render
const next = (image, loading, error) => (prev) =>
  prev.image === image && prev.loading === loading && prev.error === error
    ? prev
    : { image, loading, error };

const useImageCacheIndexedDb = (imageUrl) => {
  const [state, setState] = useState(() => initialState(imageUrl));

  useEffect(() => {
    let active = true;

    if (!imageUrl) { setState(next(null, false, true)); return; }

    if (memoryCache.has(imageUrl)) {
      setState(next(memoryCache.get(imageUrl), false, false));
      return;
    }

    setState(next(null, true, false));
    resolveImage(imageUrl)
      .then((src) => active && setState(next(src, false, false)))
      .catch(()   => active && setState(next(null, false, true)));

    return () => { active = false; };
  }, [imageUrl]);

  return state;   // { image, loading, error }
};

export default useImageCacheIndexedDb;